import { z } from 'zod';
import {
  VOLL_WALLET_MAX_LIST,
  VollWalletCurrencyAmountSchema,
  VollWalletMoneyInputSchema,
} from './voll_wallet_money.js';
import {
  VollWalletAdditionalFieldSchema,
  VollWalletAdditionalFieldValueSchema,
  VollWalletRefSchema as RefSchema,
} from './voll_wallet_pix.js';

/** A piece of the allocation: the BFF checks the id and sends the server's name to the legacy app;
 *  a `name` from the client is accepted and ignored (an older bundle sends it). */
const AllocationRefSchema = z
  .object({ id: z.string().min(1), name: z.string().optional() })
  .strict();

/** O comprovante já guardado (`POST /voll-wallet/receipts`): o id e a URL que o documents-api
 *  devolveu. O reembolso leva os dois, como o app legado. */
export const VollWalletReceiptSchema = z.object({ id: z.string().min(1), url: z.url() }).strict();
export type VollWalletReceipt = z.infer<typeof VollWalletReceiptSchema>;

/** `GET /voll-wallet/refund/form?walletId=` — what a refund on a wallet requires, like Pix: the
 *  wallet's categories, the suggested allocation (the wallet's when it defines one, otherwise the
 *  traveler profile's; `null` = none — the traveler can change it), the wallet's motive (`null` =
 *  the traveler picks from `motives`), the fields the company requires (the expenses-api refuses
 *  the refund without them) and whether the wallet refunds above its balance. */
export const VollWalletRefundFormSchema = z
  .object({
    categories: z.array(RefSchema),
    company: RefSchema.nullable(),
    costCenter: RefSchema.nullable(),
    /** Where the suggested allocation comes from: the wallet's active credit or the traveler
     *  profile (#6943). The schema is strict and an older bundle would refuse the whole form, so
     *  it only goes to the client that asks for it (`?origin=1`). */
    allocationOrigin: z.enum(['wallet', 'profile']).optional(),
    motive: RefSchema.nullable(),
    motives: z.array(RefSchema),
    additionalFields: z.array(VollWalletAdditionalFieldSchema),
    /** `true`: the wallet accepts refunds above its balance (the BFF does not refuse with
     *  `insufficient_balance`). */
    refundWithoutBalance: z.boolean(),
  })
  .strict();
export type VollWalletRefundForm = z.infer<typeof VollWalletRefundFormSchema>;

/** `GET /voll-wallet/refund/bank-data?companyId=` — whether the traveler's refund account lets
 *  the company pay the refund (the expenses-api only requires one when the company pays by Pix).
 *  The refund's Details page checks it before going on, as the legacy app (#6609); `title` and
 *  `message` are the expenses-api's, for an invalid account. */
export const VollWalletRefundBankDataSchema = z
  .object({ valid: z.boolean(), title: z.string().nullable(), message: z.string().nullable() })
  .strict();
export type VollWalletRefundBankData = z.infer<typeof VollWalletRefundBankDataSchema>;

/** Um recibo em moeda estrangeira: o valor na moeda, o câmbio (reais por unidade) e as taxas em
 *  %, como o expenses-api os recebe (`ExpenseCurrencyInput`). */
export const VollWalletRefundCurrencySchema = z
  .object({
    code: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .refine((c) => c !== 'BRL', 'foreign currency only'),
    amount: VollWalletCurrencyAmountSchema,
    exchange: z.number().positive(),
    tax: z.number().min(0).max(100).optional(),
  })
  .strict();
export type VollWalletRefundCurrency = z.infer<typeof VollWalletRefundCurrencySchema>;

/** O valor em reais de um recibo em moeda estrangeira, na conta do expenses-api
 *  (`calculateTotalReceipt`): valor × câmbio, mais as taxas, em centavos. */
export function refundAmountInReal(currency: VollWalletRefundCurrency): number {
  const sum = currency.amount * currency.exchange * (1 + (currency.tax ?? 0) / 100);
  return Math.round(sum * 100) / 100;
}

/** Um recibo além do primeiro no mesmo reembolso ("Adicionar outro recibo"). */
const ExtraReceiptSchema = z
  .object({
    categoryId: z.string().min(1),
    amount: VollWalletMoneyInputSchema,
    date: z.iso.date(),
    receipt: VollWalletReceiptSchema,
    currency: VollWalletRefundCurrencySchema.optional(),
  })
  .strict();

/** `POST /voll-wallet/refunds` — a refund for the traveler themself, in reais: the first receipt
 *  in the top-level fields and the others in `moreReceipts` (up to 10 in total). Categories belong
 *  to the wallet; the BFF checks the wallet, the categories, the motive and the balance before
 *  creating it. The web-chat sends one refund per receipt (each approved separately) and no
 *  longer uses `moreReceipts`, which stays for the old bundle. */
export const VollWalletRefundRequestSchema = z
  .object({
    walletId: z.string().min(1),
    categoryId: z.string().min(1),
    amount: VollWalletMoneyInputSchema,
    /** O dia da despesa. */
    date: z.iso.date(),
    receipt: VollWalletReceiptSchema,
    /** O primeiro recibo em moeda estrangeira; `amount` é então o valor em reais. */
    currency: VollWalletRefundCurrencySchema.optional(),
    moreReceipts: z.array(ExtraReceiptSchema).max(9).optional(),
    /** The allocation, by id: the BFF checks it against the traveler's home approval (#5819) and
     *  sends the server's names to the legacy app. */
    company: AllocationRefSchema,
    costCenter: AllocationRefSchema,
    /** The allocation's project, when the client uses projects; checked against the company's. */
    project: AllocationRefSchema.optional(),
    /** Required, as in the legacy app: without it the wallets-api does not debit the wallet and
     *  the voucher is never created. With the name: the motive list is paginated, and the BFF
     *  checks it by searching for it; the wallet's motive wins over it. */
    motive: RefSchema,
    observation: z.string().trim().max(500).optional(),
    /** The fields the wallet requires (the form's `additionalFields`), always sent; empty when
     *  the wallet asks for none. */
    additionalFields: z.array(VollWalletAdditionalFieldValueSchema).max(VOLL_WALLET_MAX_LIST),
    /** The colleague the refund is for (their wallet, allocation and bank account), as the
     *  legacy app allows; absent = the traveler themself. The BFF checks they are a user of the
     *  requester's client. */
    travelerId: z.string().min(1).max(64).optional(),
  })
  .strict();
export type VollWalletRefundRequest = z.infer<typeof VollWalletRefundRequestSchema>;

export const VollWalletRefundCreatedSchema = z.object({ id: z.string().min(1) }).strict();
export type VollWalletRefundCreated = z.infer<typeof VollWalletRefundCreatedSchema>;

/**
 * Os erros que a tela do reembolso distingue. `bank_data_missing` traz o título e a mensagem
 * do expenses-api (o cadastro bancário que falta); `wallet_unavailable`, a carteira que não
 * aceita reembolso agora (bloqueada, sem o meio, de outro viajante); `insufficient_balance`, o
 * valor acima do saldo numa carteira que não reembolsa sem saldo; `refund_rejected`, a recusa
 * do expenses-api (comprovante já usado, viajante inativo…), que só chega como texto traduzido.
 */
export const VollWalletRefundErrorSchema = z.discriminatedUnion('error', [
  z
    .object({
      error: z.literal('bank_data_missing'),
      title: z.string().nullable(),
      message: z.string().nullable(),
    })
    .strict(),
  z.object({ error: z.literal('wallet_unavailable') }).strict(),
  z.object({ error: z.literal('category_unavailable') }).strict(),
  /** The picked motive is no longer in the client's list. */
  z.object({ error: z.literal('motive_unavailable') }).strict(),
  z.object({ error: z.literal('insufficient_balance') }).strict(),
  z.object({ error: z.literal('refund_rejected') }).strict(),
  /** O wallets-api falhou sem recusar: não dá para afirmar que nada foi feito (#5688). */
  z.object({ error: z.literal('outcome_unknown') }).strict(),
  /** O agente de auditoria tem prestação de contas aberta com o viajante. */
  z.object({ error: z.literal('audit_blocked') }).strict(),
  /** O termo da política da wallet ainda não foi aceito. */
  z.object({ error: z.literal('policy_pending') }).strict(),
  /** A empresa ou o centro de custo não é permitido ao viajante. */
  z.object({ error: z.literal('allocation_invalid') }).strict(),
  /** O centro de custo foi inativado (o padrão do cadastro ou a carteira ainda apontam para ele):
   *  o viajante escolhe outro (#7167). */
  z.object({ error: z.literal('cost_center_inactive') }).strict(),
  /** O `validateAmount` do wallets-api recusou o total na data do recibo (fraude, saldo, regra
   *  retroativa, teto da política, conciliação pendente). */
  z.object({ error: z.literal('amount_not_allowed') }).strict(),
  /** The colleague is not a user of the requester's client. */
  z.object({ error: z.literal('traveler_not_allowed') }).strict(),
]);
export type VollWalletRefundError = z.infer<typeof VollWalletRefundErrorSchema>;

/** `GET /voll-wallet/refund/currencies` — as moedas do reembolso em moeda estrangeira. */
export const VollWalletRefundCurrenciesSchema = z
  .object({
    currencies: z.array(
      z
        .object({
          code: z.string().regex(/^[A-Z]{3}$/),
          name: z.string(),
          symbol: z.string().nullable(),
        })
        .strict(),
    ),
  })
  .strict();
export type VollWalletRefundCurrencies = z.infer<typeof VollWalletRefundCurrenciesSchema>;
